import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, mediaUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { SectionTitle, EmptyState, Loader, Card, StyleTag } from "../components/ui-bits";
import { STYLES, KEYS } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Music, Plus, Play, Pause, Trash2, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MyProductions() {
  const { track, playing, play } = usePlayer();
  const [prods, setProds] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ title: "", style: "Trap", bpm: "", key: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const audioInput = useRef();

  const load = () => api.get("/productions/mine").then((r) => setProds(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Ajoute un titre");
    if (!audio) return toast.error("Sélectionne un fichier audio (MP3/WAV)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("style", form.style);
      if (form.bpm) fd.append("bpm", form.bpm);
      if (form.key) fd.append("key", form.key);
      fd.append("audio", audio);
      if (cover) fd.append("cover", cover);
      await api.post("/productions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Production ajoutée !");
      setModal(false); setForm({ title: "", style: "Trap", bpm: "", key: "" }); setAudio(null); setCover(null);
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette production ?")) return;
    await api.delete(`/productions/${id}`);
    setProds((p) => p.filter((x) => x.id !== id));
    toast.success("Production supprimée");
  };

  if (prods === null) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <SectionTitle sub="Ta bibliothèque de prods, prête à être proposée.">Mes productions</SectionTitle>
        <button onClick={() => setModal(true)} data-testid="open-upload-modal-button"
          className="shrink-0 -mt-4 flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold text-sm glow-violet transition-all">
          <Plus size={17} /> <span className="hidden sm:inline">Uploader</span>
        </button>
      </div>

      {prods.length === 0 ? (
        <EmptyState icon={Music} title="Aucune production" desc="Uploade tes prods pour pouvoir les proposer aux artistes."
          action={<button onClick={() => setModal(true)} className="px-5 py-2.5 rounded-full bg-violet-600 hover:bg-violet-500 font-medium text-sm">Uploader ma première prod</button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {prods.map((p) => {
            const cov = mediaUrl(p, "image");
            const isCur = track && track.id === `mine-${p.id}`;
            return (
              <Card key={p.id} className="p-3 flex gap-3 items-center" data-testid={`production-${p.id}`}>
                <button onClick={() => play({ id: `mine-${p.id}`, title: p.title, subtitle: p.style || "", production: p, cover: cov })}
                  data-testid={`play-production-${p.id}`}
                  className="relative w-14 h-14 rounded-xl overflow-hidden shrink-0">
                  {cov ? <img src={cov} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-violet-700 to-purple-900 flex items-center justify-center"><Music size={18} /></div>}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    {isCur && playing ? <Pause size={18} fill="white" /> : <Play size={18} fill="white" className="ml-0.5" />}
                  </div>
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{p.title}</p>
                  <p className="text-[11px] font-mono text-zinc-500">{[p.style, p.bpm && `${p.bpm} BPM`, p.key].filter(Boolean).join(" · ")}</p>
                </div>
                <button onClick={() => del(p.id)} data-testid={`delete-production-${p.id}`} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="glass border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display">Nouvelle production</DialogTitle></DialogHeader>
          <form onSubmit={upload} className="space-y-4">
            <input data-testid="prod-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de la prod"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
            <div>
              <p className="text-xs text-zinc-400 mb-2">Style</p>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">{STYLES.map((s) => <StyleTag key={s} active={form.style === s} onClick={() => setForm({ ...form, style: s })}>{s}</StyleTag>)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="prod-bpm-input" type="number" value={form.bpm} onChange={(e) => setForm({ ...form, bpm: e.target.value })} placeholder="BPM"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-3 text-sm font-mono focus:border-violet-500 outline-none" />
              <select value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-3 text-sm font-mono focus:border-violet-500 outline-none">
                {KEYS.map((k) => <option key={k} value={k}>{k || "Tonalité"}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs text-zinc-400 mb-2">Fichier audio (MP3 / WAV)</p>
              <input ref={audioInput} data-testid="prod-audio-input" type="file" accept=".mp3,.wav,audio/*" onChange={(e) => setAudio(e.target.files[0])} className="hidden" />
              <button type="button" onClick={() => audioInput.current.click()}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed text-sm transition-colors ${audio ? "border-violet-500 text-violet-300" : "border-zinc-600 text-zinc-400 hover:border-violet-600"}`}>
                <UploadCloud size={18} /> {audio ? audio.name : "Choisir un fichier"}
              </button>
            </div>
            <div>
              <p className="text-xs text-zinc-400 mb-2">Pochette (optionnel)</p>
              <input data-testid="prod-cover-input" type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0])} className="text-xs text-zinc-400" />
            </div>
            <button type="submit" disabled={busy} data-testid="submit-production-button"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold transition-all disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} Ajouter la production
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
